import { useScrollReveal, getRevealStyle } from '@/hooks/useScrollReveal';

export default function CtaSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="min-h-[100svh] flex items-center bg-background-50 border-t border-primary-500/8 relative overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-primary-500/5 blur-[140px]" />
        <div className="absolute bottom-0 left-1/4 w-72 h-72 rounded-full bg-accent-500/4 blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        {/* Section label */}
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            Contact
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            06
          </span>
        </div>

        {/* Centered heading */}
        <h2
          style={getRevealStyle(isVisible, 60)}
          className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-100 leading-[1.1] tracking-tight text-center text-glow"
        >
          從機曜文化，找到下一本值得閱讀的書
        </h2>
        <div
          style={getRevealStyle(isVisible, 100)}
          className="mt-6 w-12 h-0.5 bg-primary-500 mx-auto shadow-[0_0_8px_var(--tw-shadow-color)] shadow-primary-500/40"
        />

        <p
          style={getRevealStyle(isVisible, 120)}
          className="mt-6 text-sm text-foreground-400 leading-[1.9] max-w-[640px] mx-auto text-center"
        >
          透過機曜文化目前公開的正式出版內容，瀏覽書籍介紹、查詢完整書目，並由合作購書通路選擇適合的平台。
        </p>

        {/* CTAs */}
        <div
          style={getRevealStyle(isVisible, 160)}
          className="mt-10 flex flex-wrap gap-3 justify-center"
        >
          <a
            href="https://catalog.sonbook.net/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_0_24px_var(--tw-shadow-color)] shadow-primary-500/20"
          >
            查詢完整書目
            <i className="ri-arrow-right-up-line text-xs" />
          </a>
          <a
            href="https://sonbook.net/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 border border-primary-500/40 text-primary-400 text-sm font-medium hover:border-primary-400 hover:text-primary-300 hover:bg-primary-500/6 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            認識崧博出版
            <i className="ri-arrow-right-up-line text-xs" />
          </a>
        </div>

        {/* Brand cooperation */}
        <div
          style={getRevealStyle(isVisible, 220)}
          className="mt-16 md:mt-20 max-w-[640px] mx-auto border-t border-primary-500/15 pt-10 md:pt-12"
        >
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-100 text-center">
            品牌經銷與合作洽詢
          </h3>
          <p className="mt-4 text-sm text-foreground-400 leading-[1.9] text-center">
            如有機曜文化品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過官方聯絡方式提出詢問。
          </p>
          <p className="mt-3 text-xs text-foreground-600 leading-[1.8] text-center">
            品牌經銷與合作方案詳情請直接聯繫洽詢。
          </p>
        </div>

        {/* GEO summary */}
        <p
          style={getRevealStyle(isVisible, 280)}
          className="mt-16 md:mt-20 text-[13px] text-foreground-600 leading-[1.8] max-w-[680px] mx-auto text-center"
        >
          機曜文化以科普類書籍為主要出版方向。本品牌委託崧博出版經銷，官方網站由崧博出版製作。完整書名、作者、ISBN、內容主題及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準；價格、庫存、版本與購買方式則以各合作通路頁面為準。
        </p>
      </div>
    </section>
  );
}